import { Link, useNavigate } from "react-router-dom";
import { MapPin, Phone, Mail, Clock } from "lucide-react";
import { useLang } from "@/context/LanguageContext";
import { LANGS, pathFor } from "@/i18n/translations";
import { siteConfig, openingHours } from "@/config/site";

export const Footer = () => {
  const { lang, pageKey, t } = useLang();
  const nav = useNavigate();
  const year = new Date().getFullYear();

  return (
    <footer data-testid="site-footer" className="bg-[#08080A] border-t border-white/10 pt-16 pb-28 md:pb-10">
      <div className="mx-auto max-w-[1400px] px-5 md:px-8">
        <div className="grid grid-cols-2 md:grid-cols-4 gap-10 md:gap-8">
          {/* Brand + company */}
          <div className="col-span-2 md:col-span-1">
            <div className="flex items-center mb-4">
              <img src={siteConfig.logo} alt={siteConfig.companyName} className="h-16 w-auto object-contain" />
            </div>
            <p className="text-sm text-zinc-500 leading-relaxed max-w-xs">{t.footer.tagline}</p>
          </div>

          <div>
            <h4 className="text-xs uppercase tracking-[0.2em] text-zinc-500 mb-4">{t.footer.company}</h4>
            <ul className="space-y-2.5 text-sm">
              <li><Link to={pathFor(lang, "about")} className="text-zinc-300 hover:text-white transition-colors">{t.footer.companyLinks[0]}</Link></li>
              <li><Link to={pathFor(lang, "contact")} className="text-zinc-300 hover:text-white transition-colors">{t.footer.companyLinks[1]}</Link></li>
            </ul>
          </div>

          <div>
            <h4 className="text-xs uppercase tracking-[0.2em] text-zinc-500 mb-4">{t.footer.services}</h4>
            <ul className="space-y-2.5 text-sm">
              <li><Link to={pathFor(lang, "mechanical")} className="text-zinc-300 hover:text-white transition-colors">{t.footer.serviceLinks[0]}</Link></li>
              <li><Link to={pathFor(lang, "mechanical")} className="text-zinc-300 hover:text-white transition-colors">{t.footer.serviceLinks[1]}</Link></li>
              <li><Link to={pathFor(lang, "mechanical")} className="text-zinc-300 hover:text-white transition-colors">{t.footer.serviceLinks[2]}</Link></li>
              <li><Link to={pathFor(lang, "tires")} className="text-zinc-300 hover:text-white transition-colors">{t.footer.serviceLinks[3]}</Link></li>
            </ul>
          </div>

          <div className="col-span-2 md:col-span-1">
            <h4 className="text-xs uppercase tracking-[0.2em] text-zinc-500 mb-4">{t.footer.contact}</h4>
            <ul className="space-y-3 text-sm text-zinc-300">
              <li className="flex items-start gap-2.5">
                <MapPin size={16} className="mt-0.5 shrink-0" style={{ color: "var(--accent)" }} />
                <span>{siteConfig.address.street}, {siteConfig.address.city}</span>
              </li>
              <li>
                <a href={siteConfig.phoneHref} data-testid="footer-phone" className="flex items-center gap-2.5 hover:text-white transition-colors">
                  <Phone size={16} style={{ color: "var(--accent)" }} /> {siteConfig.phone}
                </a>
              </li>
              <li>
                <a href={`mailto:${siteConfig.email}`} data-testid="footer-email" className="flex items-center gap-2.5 hover:text-white transition-colors">
                  <Mail size={16} style={{ color: "var(--accent)" }} /> {siteConfig.email}
                </a>
              </li>
              <li className="flex items-start gap-2.5">
                <Clock size={16} className="mt-0.5 shrink-0" style={{ color: "var(--accent)" }} />
                <span>
                  {openingHours.map((h) => (
                    <span key={h.dayKey} className="block">
                      {t.hours[h.dayKey]}: {h.time === "closed" ? t.hours.closed : h.time}
                    </span>
                  ))}
                </span>
              </li>
            </ul>
          </div>
        </div>

        <div className="mt-8 pt-4 border-t border-white/5">
          <ul className="flex flex-wrap gap-x-6 gap-y-2 text-xs text-zinc-500">
            {t.footer.usefulLinks.map((l) => (
              <li key={l}><span className="hover:text-zinc-300 transition-colors cursor-pointer">{l}</span></li>
            ))}
          </ul>
        </div>

        <div className="mt-8 pt-6 border-t border-white/5 flex flex-col md:flex-row items-center justify-between gap-4">
          <p className="text-xs text-zinc-500">© {year} {siteConfig.companyName}. {t.footer.rights}</p>
          <div className="flex items-center gap-1 text-xs font-semibold">
            {LANGS.map((l) => (
              <button key={l} onClick={() => nav(pathFor(l, pageKey))} data-testid={`footer-lang-${l}`}
                className={`px-1.5 uppercase ${lang === l ? "text-white" : "text-zinc-500 hover:text-zinc-300"}`}>
                {l}
              </button>
            ))}
          </div>
        </div>
      </div>
    </footer>
  );
};
